import type { ExportFilter, ExportProfile } from '@messe/shared'
import { apiRequest } from './api.js'

/** What a profile looks like when it is being written. */
export type ExportProfileInput = Omit<
  ExportProfile,
  'id' | 'createdByUserId' | 'createdAt' | 'updatedAt' | 'deletedAt'
>

export interface AvailableColumns {
  core: { field: string; label: string }[]
  attributes: { key: string; label: string; type: string; group: string | null }[]
}

export interface ExportPreview {
  header: string[]
  rows: string[][]
  totalMatching: number
}

export interface ExportRunResult {
  runId: string
  fileName: string
  rowCount: number
  photoCount: number
  byteSize: number
  unmappableCharacters: string[]
  missingPhotos: string[]
  /** The file itself, base64. */
  content: string
}

export interface ExportRunSummary {
  id: string
  profileName: string
  status: string
  rowCount: number | null
  photoCount: number | null
  fileName: string | null
  byteSize: number | null
  errorMessage: string | null
  createdAt: string
  finishedAt: string | null
}

export function listAvailableColumns(): Promise<AvailableColumns> {
  return apiRequest<AvailableColumns>('/exports/columns')
}

export function listExportProfiles(): Promise<{ profiles: ExportProfile[] }> {
  return apiRequest<{ profiles: ExportProfile[] }>('/exports/profiles')
}

export function createExportProfile(profile: ExportProfileInput): Promise<ExportProfile> {
  return apiRequest<ExportProfile>('/exports/profiles', { method: 'POST', body: profile })
}

export function updateExportProfile(
  id: string,
  changes: Partial<ExportProfileInput>,
): Promise<ExportProfile> {
  return apiRequest<ExportProfile>(`/exports/profiles/${id}`, { method: 'PATCH', body: changes })
}

export function duplicateExportProfile(id: string, name: string): Promise<ExportProfile> {
  return apiRequest<ExportProfile>(`/exports/profiles/${id}/duplicate`, {
    method: 'POST',
    body: { name },
  })
}

export async function deleteExportProfile(id: string): Promise<void> {
  await apiRequest(`/exports/profiles/${id}`, { method: 'DELETE' })
}

export function previewExport(
  profile: ExportProfileInput,
  filter: ExportFilter,
): Promise<ExportPreview> {
  return apiRequest<ExportPreview>('/exports/preview', {
    method: 'POST',
    body: { profile, filter },
  })
}

export function runExport(profileId: string, filter: ExportFilter): Promise<ExportRunResult> {
  return apiRequest<ExportRunResult>('/exports/runs', {
    method: 'POST',
    body: { profileId, filter },
  })
}

export function listExportRuns(): Promise<{ runs: ExportRunSummary[] }> {
  return apiRequest<{ runs: ExportRunSummary[] }>('/exports/runs')
}

export function repeatExportRun(id: string): Promise<ExportRunResult> {
  return apiRequest<ExportRunResult>(`/exports/runs/${id}/repeat`, { method: 'POST' })
}

/**
 * Hands the file to the browser.
 *
 * The bytes arrive base64-encoded in the response rather than as a URL, so the
 * download is assembled here. An anchor with a blob URL is the only way to
 * suggest a file name from JavaScript, and the URL is released afterwards —
 * without that, every export leaks its own size until the tab is closed.
 */
export function downloadExport(result: { fileName: string; content: string }): void {
  const binary = atob(result.content)
  const bytes = new Uint8Array(binary.length)

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }

  const url = URL.createObjectURL(new Blob([bytes], { type: mimeTypeFor(result.fileName) }))
  const anchor = document.createElement('a')

  anchor.href = url
  anchor.download = result.fileName
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()

  URL.revokeObjectURL(url)
}

function mimeTypeFor(fileName: string): string {
  if (fileName.endsWith('.json')) return 'application/json'
  if (fileName.endsWith('.zip')) return 'application/zip'
  return 'text/csv'
}

/** The transform defaults a new column starts with. */
export function defaultTransform(): ExportProfile['columns'][number]['transform'] {
  return {
    dateFormat: 'dd.MM.yyyy',
    decimalSeparator: ',',
    thousandsSeparator: '',
    prefix: '',
    suffix: '',
    emptyValue: '',
    caseTransform: 'none',
    multiValueSeparator: '; ',
    valueListRendering: 'label',
    metadataKey: null,
    userRendering: 'name',
    booleanTrue: 'Ja',
    booleanFalse: 'Nein',
  }
}

/**
 * A new profile, set up for German Excel.
 *
 * Semicolon, CRLF and a byte-order mark: the combination that opens correctly
 * by double-click on a German Windows machine, which is where these files go.
 */
export function emptyProfile(): ExportProfileInput {
  return {
    name: '',
    description: null,
    format: 'csv',
    columns: [],
    delimiter: ';',
    quoteChar: '"',
    quoting: 'minimal',
    lineEnding: '\r\n',
    encoding: 'utf-8-bom',
    includeHeader: true,
    includePhotos: false,
  }
}

export function emptyFilter(): ExportFilter {
  return {
    eventIds: [],
    supplierItemIds: [],
    createdByUserIds: [],
    capturedFrom: null,
    capturedTo: null,
    includeNeedsReview: true,
  }
}
